<template>
  <v-layout column justify-space-between class="manage-ruleset white">
    <div>
      <!-- Title and Actions -->
      <v-flex xs12 class="py-0">
        <v-toolbar color="secondary" class="elevation-0">
          <!-- Back Button -->
          <v-btn icon flat class="primary--text text--lighten-5" @click="$router.go(-1)">
            <v-icon>arrow_back</v-icon>
          </v-btn>
          <v-toolbar-title class="white--text subheading ttu">
            <router-link :to="{ name: 'XVATRulesSearch' }" class="white--text link dim">{{ messages.label.METADATA_RULES
            }}</router-link>/
            <span v-if="isEditMode">{{ messages.label.EDIT_RULE_SET }} - "{{ currentRuleSet.title }}"</span>
            <span v-else>{{ messages.label.CREATE_RULE_SET }}</span>
          </v-toolbar-title>
          <v-spacer></v-spacer>
        </v-toolbar>
      </v-flex>
      <!-- Main Content -->
      <v-flex xs12>
        <v-card flat>
          <!-- Header with tabs and actions -->
          <v-card-title class="grey lighten-4 px-3 py-0">
            <v-tabs v-model="activeTab" @change="ensureWindow" color="grey lighten-4">
              <v-tabs-slider color="accent"></v-tabs-slider>
              <v-tab v-for="item in tabItems" :key="item" class="secondary--text">{{ item }}</v-tab>
              <v-spacer></v-spacer>
            </v-tabs>
            <v-spacer></v-spacer>
            <v-btn color="primary" round @click="showTestRuleModal()" v-if="isEditMode"
              :disabled="createOrUpdateInProgress" :loading="createOrUpdateInProgress">{{ messages.label.TEST }}
            </v-btn>
          </v-card-title>
          <!-- Main content of rule (info, conditions, actions) -->
          <v-card-text class="pt-1">
            <!-- Loading indicator -->
            <v-progress-linear color="accent" v-show="dataLoading" height="2" :indeterminate="true"></v-progress-linear>
            <!-- Error Alert -->
            <v-flex xs12 v-if="errorAlert">
              <v-alert v-model="errorAlert" dismissible type="error">{{ errorMessage }}</v-alert>
            </v-flex>
            <v-tabs-items v-model="activeTab" ref="window">
              <v-tab-item>
                <RuleSetInfo />
              </v-tab-item>
              <v-tab-item>
                <RuleSetRules />
              </v-tab-item>
              <v-tab-item v-if="isEditMode">
                <JSONView :json="currentRuleSetJSON" />
              </v-tab-item>
            </v-tabs-items>
          </v-card-text>
        </v-card>
      </v-flex>
      <!-- Rule Test Dialog -->
      <XVATRuleTest v-model="ruleTestDialog" :rule-obj="ruleObjectToTest" :rule-category="'ruleSet'" />
      <!-- Snackbar -->
      <v-flex xs12>
        <v-snackbar :bottom="true" :timeout="snackbar.timeout" :color="snackbar.color" v-model="snackbar.show">
          {{ snackbar.message }}
          <v-btn flat color="secondary" @click.native="snackbar.show = false">{{ messages.label.OK }}</v-btn>
        </v-snackbar>
      </v-flex>
    </div>
    <!-- Card actions -->
    <v-card-actions class="grey lighten-4 px-3">
      <v-btn color="grey lighten-1 white--text" @click="cancel()">{{ messages.label.CANCEL }}</v-btn>
      <v-btn color="primary" v-if="isEditMode" :disabled="createOrUpdateInProgress" :loading="createOrUpdateInProgress"
        @click="validateForUpdateRuleSet()">{{ messages.label.SAVE }}</v-btn>
      <v-btn color="primary" v-else :disabled="createOrUpdateInProgress" :loading="createOrUpdateInProgress"
        @click="validateForCreateRuleSet()">{{ messages.label.CREATE }}</v-btn>
      <v-spacer></v-spacer>
      <v-btn color="error" v-if="isEditMode" @click="showDeleteDialog()">
        <span>{{ messages.label.DELETE }}</span>
      </v-btn>
      <!-- Delete Dialog -->
      <v-dialog v-model="deleteDialog" width="500">
        <v-card>
          <v-card-title class="subheading ttu">{{ messages.label.DELETE }}</v-card-title>
          <v-card-text>
            <!-- Error Alert -->
            <v-flex xs12 v-if="deleteErrorAlert">
              <v-alert v-model="deleteErrorAlert" type="error">{{ deleteErrorMessage }}</v-alert>
            </v-flex>
            <v-flex xs12 v-else>
              <p>
                {{ messages.info.DELETE_RULE_1 }}
                <b>{{ currentRuleSet.title }}</b> ?
              </p>
              <p v-html="messages.info.DELETE_RULE_2"></p>
            </v-flex>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <template v-if="deleteErrorAlert">
              <v-btn color="secondary lighten-1 white--text" @click="cancelDelete">Close</v-btn>
            </template>
            <template v-else>
              <v-btn color="grey lighten-1 white--text" @click="cancelDelete">{{ messages.label.CANCEL }}</v-btn>
              <v-btn color="error" :disabled="deletingRule" :loading="deletingRule" @click="deleteRuleSet()">{{
                  messages.label.YES
              }}</v-btn>
            </template>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card-actions>
  </v-layout>
</template>

<script>
import { mapState, mapMutations } from "vuex"
import XVATRulesService from "@/XVAT/XVATRulesService"
import * as xvatDataService from "@/XVAT/XVATDataService"
import * as utils from "@/Shared/utils"
import AppService from "@/AppService"
import RuleQuickView from "@/XVAT/Rule/RuleQuickView"
import RuleSetRules from "@/XVAT/RuleSet/RuleSetRules"
import RuleSetInfo from "@/XVAT/RuleSet/RuleSetInfo"
import JSONView from "@/XVAT/JSONView"
import XVATRuleTest from "@/XVAT/XVATRuleTest"
import messages from "@/XVAT/messages.json"
import { SET_RULESET_RULES } from "@/store/constants/mutation-types"

export default {
  name: "XVATManageRuleSet",
  components: {
    RuleSetInfo,
    RuleSetRules,
    RuleQuickView,
    JSONView,
    XVATRuleTest
  },
  data() {
    return {
      activeTab: null,
      isEditMode: false,
      errorAlert: false,
      errorMessage: "",
      ruleObjectToTest: {},
      ruleTestDialog: false,
      validationStatus: {
        info: false,
        rules: true
      },
      ruleSetAction: "",
      ruleSetInfoData: {},
      currentRuleSet: {},
      currentRuleSetJSON: {},
      snackbar: {
        show: false,
        timeout: 5000,
        message: "",
        color: "error"
      },
      dataLoading: false,
      createOrUpdateInProgress: false,
      deleteDialog: false,
      deletingRule: false,
      deleteErrorAlert: false,
      deleteErrorMessage: "",
      windowTimeout: null
    }
  },
  watch: {
    validationStatus: {
      handler(newValue) {
        if (newValue.info === true && newValue.rules === true) {
          switch (this.ruleSetAction) {
            case "create":
              this.createRuleSet()
              break
            case "update":
              this.updateRuleSet()
              break
          }
        }
      },
      deep: true
    }
  },
  computed: {
    ...mapState("xvat", ["ruleSetRulesList"]),
    tabItems() {
      if (this.isEditMode) {
        return ["INFO", "RULES", "{ JSON }"]
      } else {
        return ["INFO", "RULES"]
      }
    }
  },
  methods: {
    ...mapMutations("xvat", [SET_RULESET_RULES]),
    /**
     * validateForUpdateRuleSet - Begin validation for update ruleset
     */
    validateForUpdateRuleSet() {
      this.ruleSetAction = "update"
      this.validateAll()
    },

    /**
     * validateForCreateRuleSet - Begin validation for create ruleset
     */
    validateForCreateRuleSet() {
      this.ruleSetAction = "create"
      this.validateAll()
    },

    /**
     * resetValidationStatus - Reset validation status
     */
    resetValidationStatus() {
      this.validationStatus = {
        info: false,
        rules: false
      }
    },

    /**
     * resetData - Reset data
     */
    resetData() {
      if (this.$lodash.isNull(this.activeTab)) {
        this.activeTab = 0
      }
      this.isEditMode = false
      this.ruleSetAction = ""
      this.ruleSetInfoData = {}
      this.currentRuleSet = {}
      this.currentRuleSetJSON = {}
      this.errorAlert = false
      this.errorMessage = ""
      this.SET_RULESET_RULES([])
      this.resetValidationStatus()
    },

    /**
     * validateAll - Validate ruleset info and rules
     */
    validateAll() {
      XVATRulesService.$emit("event:validateRuleSetInfo")
      XVATRulesService.$emit("event:validateRuleSetRules")
    },

    /**
     * validateRuleSetInfo - Handles validation of ruleset info view
     * @param {boolean} result
     * @param {object} ruleSetInfoData
     */
    validateRuleSetInfo(result, ruleSetInfoData) {
      this.validationStatus.info = result
      this.ruleSetInfoData = ruleSetInfoData
      if (!this.validationStatus.info) {
        this.activeTab = 0 // Show Info tab
      }
    },

    /**
     * validateRuleConditions - Handles validation of rules view
     */
    validateRuleSetRules(result) {
      this.validationStatus.rules = result
      if (!this.validationStatus.rules) {
        this.activeTab = 1 // Show Info tab
      }
    },

    /**
     * createRule - Create new ruleset
     */
    createRuleSet() {
      this.createOrUpdateInProgress = true
      this.ruleSetInfoData.title = this.$lodash.trim(this.ruleSetInfoData.title)
      this.ruleSetInfoData.description = this.$lodash.trim(this.ruleSetInfoData.description)

      let ruleSetData = {
        title: this.ruleSetInfoData.title,
        type: this.ruleSetInfoData.type,
        disabled: !this.ruleSetInfoData.enabled,
        description: encodeURI(this.ruleSetInfoData.description),
        inputItems: this.getRuleSetRuleIds(),
        locked: false
      }
      xvatDataService
        .createRuleSet(ruleSetData)
        .then(response => {
          let ruleSetId = response.data.id
          this.showSnackBar(
            "success",
            this.messages.info.RULESET_CREATE_SUCCESS
          )
          this.$router.replace({
            name: "XVATManageRuleSet",
            params: { id: ruleSetId }
          })
        })
        .catch(error => {
          this.resetValidationStatus()
          this.showError(error)
        })
        .finally(() => {
          this.createOrUpdateInProgress = false
          XVATRulesService.$emit("event:refreshRulesList")
        })
    },

    /**
     * updateRule - Update existing ruleset
     */
    updateRuleSet() {
      this.createOrUpdateInProgress = true
      // Update current rule data
      this.currentRuleSet.title = this.$lodash.trim(this.ruleSetInfoData.title)
      this.currentRuleSet.type = this.ruleSetInfoData.type
      this.currentRuleSet.disabled = !this.ruleSetInfoData.enabled
      this.currentRuleSet.description = this.$lodash.trim(this.ruleSetInfoData.description)
      this.currentRuleSet.description = encodeURI(this.currentRuleSet.description)
      this.currentRuleSet.inputItems = this.getRuleSetRuleIds()

      //  TODO: Temp , discuss with Christian
      let ruleSetId = this.currentRuleSet.id
      let updatedRuleSet = this.$lodash.omit(this.currentRuleSet, ["id", "added", "addedByUserId", "version", "updated", "updatedByUserId", "items"])

      xvatDataService
        .updateRuleSet(ruleSetId, updatedRuleSet)
        .then(response => {
          this.showSnackBar(
            "success",
            this.messages.info.RULESET_UPDATE_SUCCESS
          )
          let ruleSetId = this.currentRuleSet.id
          this.loadRuleSetData(ruleSetId)
          return response
        })
        .catch(error => {
          this.showError(error)
        })
        .finally(() => {
          this.resetValidationStatus()
          this.createOrUpdateInProgress = false
          XVATRulesService.$emit("event:refreshRulesList")
        })
    },

    /**
     * getRuleSetRuleIds - Retrieves just the rule ids required for create / update
     */
    getRuleSetRuleIds() {
      if (this.ruleSetRulesList.length > 0) {
        return this.$lodash.map(this.ruleSetRulesList, "id")
      } else {
        return []
      }
    },

    /**
     * retreiveRuleDetails - Retrieve rule set details with given id
     * @param {string} ruleSetId
     */
    retreiveRuleSetDetails(ruleSetId) {
      this.currentRuleSet = {}
      this.currentRuleSetJSON = {}

      return xvatDataService
        .getRuleSet(ruleSetId)
        .then(response => {
          // RuleSet data from 'data' service
          if (response.data) {
            this.currentRuleSet = response.data.entries[0]
            this.currentRuleSetJSON = this.$lodash.cloneDeep(this.currentRuleSet)
            if (this.currentRuleSet.items.length > 0) {
              let ruleSetRules = XVATRulesService.addClientOnlyFields(this.currentRuleSet.items)
              this.SET_RULESET_RULES(ruleSetRules)
            }
            return this.currentRuleSet
          } else {
            // TODO: Redirect to 404
            return Promise.reject(
              new Error(`Could not find rule set with id: ${ruleSetId}`)
            )
          }
        })
        .then(response => {
          this.currentRuleSetJSON = this.$lodash.cloneDeep(response.data)
        })
        .catch(error => {
          return Promise.reject(error)
        })
    },

    /**
     * loadRuleData - Takes care of loading rule set data in the views
     * @param {string} ruleId
     */
    loadRuleSetData(ruleSetId) {
      this.dataLoading = true
      this.resetData()
      if (!this.$lodash.isUndefined(ruleSetId)) {
        // Edit mode
        this.isEditMode = true
        return this.retreiveRuleSetDetails(ruleSetId)
          .then(response => {
            // Ruleset data retrieved
            XVATRulesService.$emit(
              "event:ruleSetDataRetrieved",
              this.currentRuleSet
            )
            return response
          })
          .catch(error => {
            this.showError(error)
          })
          .finally(() => {
            this.dataLoading = false
          })
      } else {
        // Create mode
        this.isEditMode = false
        XVATRulesService.$emit("event:ruleSetDataRetrieved")
        this.dataLoading = false
        return Promise.resolve({})
      }
    },

    /**
     * showSnackBar - Show snackbar message
     * @param {string} color
     * @param {string} message
     */
    showSnackBar(color, message) {
      this.snackbar.show = true
      this.snackbar.color = color
      this.snackbar.message = message
    },

    /**
     * showError - Handles displaying error
     * @param {object} error
     */
    showError(error) {
      let isServerError = utils.isMPXError(error)
      let isClientError = utils.isClientError(error)
      if (isServerError) {
        this.errorAlert = true
        this.errorMessage = error.response.data.description
      } else if (isClientError) {
        this.errorAlert = true
        this.errorMessage = error.message
      }
    },

    /**
     * cancel - Cancel out of rule create / edit view
     */
    cancel() {
      this.$router.push({ name: "XVATRulesSearch" })
    },
    /**
     * showDeleteDialog -
     */
    showDeleteDialog() {
      this.deleteDialog = true
    },

    /**
     * cancelDelete - close delete dialog
     */
    cancelDelete() {
      this.deleteErrorAlert = false
      this.deleteErrorMessage = ""
      this.deleteDialog = false
    },

    /**
     * deleteRuleSet - Delete selected rule / ruleset / rule collection
     */
    deleteRuleSet() {
      this.deletingRule = true
      let ruleSetId = this.currentRuleSet.id
      xvatDataService
        .deleteRuleSet(ruleSetId)
        .then(() => {
          this.deleteDialog = false
          // Show Snackbar
          this.snackbar.show = true
          this.snackbar.color = "success"
          this.snackbar.message =
            this.messages.info.DELETE_SUCCESS + this.currentRuleSet.title
          // route user to search screen
          this.$router.push({ name: "XVATRulesSearch" })
        })
        .catch(error => {
          let response = error.response.data
          if (response.isException) {
            this.deleteErrorAlert = true
            if (response.responseCode === 412) {
              this.deleteErrorMessage = XVATRulesService.constructDeleteFailMsg("Rule Set", this.currentRuleSet.title)
            } else {
              this.deleteErrorMessage = response.description
            }
          }
        })
        .finally(() => {
          this.deletingRule = false
          XVATRulesService.$emit("event:refreshRulesList")
        })
    },

    /**
     * showTestRuleModal
     */
    showTestRuleModal() {
      this.ruleObjectToTest = this.currentRuleSet
      this.ruleTestDialog = true
    },

    /**
     * NOTE: Fix for auto height adjust issue of v-tabs
     * FIXME: Once fix is available, update vuetify version
     */
    ensureWindow() {
      clearTimeout(this.windowTimeout)
      this.windowTimeout = setTimeout(() => {
        this.$refs.window.internalHeight = null
        this.$refs.window.isActive = false
      }, 1000)
    }
  },
  created() {
    /**
     * Non reactive properties
     */
    this.messages = messages
    /**
     * Events
     * */
    XVATRulesService.$on(
      "event:ruleSetInfoValidation",
      this.validateRuleSetInfo
    )
    XVATRulesService.$on(
      "event:ruleSetRulesValidation",
      this.validateRuleSetRules
    )
    XVATRulesService.$on(
      "event:validateForUpdateRuleSet", () => {
        if (this.isEditMode) {
          this.validateForUpdateRuleSet()
        }
      }
    )
    AppService.$on("event:preIngestServerError", () => {
      this.showSnackBar("error", this.messages.error.SERVER_ERROR)
    })
  },

  /**
   * Triggered on first entry in to the route /xvat/rule-set
   * @param {object} to
   * @param {object} from
   * @param {Function} next
   */
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.loadRuleSetData(to.params.id)
    })
  },

  /**
   * Triggered on update to the route /xvat/rule-set
   * @param {object} to
   * @param {object} from
   * @param {Function} next
   */
  beforeRouteUpdate(to, from, next) {
    this.loadRuleSetData(to.params.id).then(() => {
      next()
    })
  },
  beforeDestroy() {
    XVATRulesService.$off(
      "event:ruleSetInfoValidation",
      this.validateRuleSetInfo
    )
    XVATRulesService.$off(
      "event:ruleSetRulesValidation",
      this.validateRuleConditions
    )
    XVATRulesService.$on("event:validateForUpdateRuleSet")
  }
}
</script>

<style scoped>
.manage-ruleset .tabs-extension {
  height: 48px;
}

.manage-ruleset {
  min-height: 90%;
}
</style>



// WEBPACK FOOTER //
// src/XVAT/RuleSet/XVATManageRuleSet.vue